import { homeRow, onBoardPawns, sameSquare } from '../board';
import { faceCode } from '../cards';
import { isCollinear } from '../scoring';
import { Pcg32 } from '../rng';
import type {
  Constants,
  Face,
  LedgerLink,
  MatchState,
  PatternName,
  PcgState,
  ScoreBreakdown,
  Side,
  Square,
} from '../types';
import type { AppraisalTarget, HiddenPattern, StampId } from './ids';
import {
  L,
  appraisal,
  carvingValue,
  counter,
  discovered,
  hasCarving,
  hasRule,
  houseRule,
  mantelOf,
  sideStatus,
  statusOf,
} from './state';
import type { ItemSpec, KitSpec, TallyOp, TallySourceKind, TallyStep } from './types';

/**
 * The Tally (LEDGER.MD §8): a chain or replay counted step by step. P is points, M the
 * multiplier, kept in tenths so ×1.5 is exact; the score is P × M rounded down.
 *
 *   1. links: landings, captures, crowning, Omens landed on
 *   2. shape: one per link, the best pattern, Full Circle, Appraisals
 *   3. cards: each link's Stamp, then Finish
 *   4. the pawn: Carvings, statuses, the side's own statuses
 *   5. the Mantel, left to right
 *   6. score = P × M
 */

export interface TallyInput {
  readonly side: Side;
  readonly source: 'chain' | 'epitaph';
  /** The pawn that moved. */
  readonly pawnId: string;
  readonly links: readonly LedgerLink[];
  readonly start: Square;
  readonly end: Square | null;
  readonly crownedAtStart: boolean;
  readonly crownedAtEnd: boolean;
  /** The top of the pile before the chain's first card (Snap). */
  readonly topBefore: Face | null;
  /** The pawn a replayed Epitaph came from (Scribe's Mark). */
  readonly epitaphSource?: string;
  /** Pile size once the chain is on it (Spilled Salt). */
  readonly pileSize: number;
}

/** Something the Tally changes when a chain is really scored (never in a preview). */
export type TallyEffect =
  | {
      readonly kind: 'brass';
      readonly amount: number;
      readonly reason: string;
      readonly cap?: { readonly key: string; readonly max: number };
    }
  | { readonly kind: 'grow'; readonly uid: string; readonly value: number }
  | { readonly kind: 'cardValue'; readonly cardId: string; readonly value: number }
  | { readonly kind: 'pawnValue'; readonly key: string; readonly value: number }
  | { readonly kind: 'shatter'; readonly cardId: string }
  | { readonly kind: 'trigger'; readonly uid: string }
  | { readonly kind: 'useSideStatus'; readonly status: 'inspired' | 'black-candle' }
  | { readonly kind: 'kindled'; readonly pawnId: string; readonly n: number };

export interface TallyResult {
  readonly breakdown: ScoreBreakdown;
  readonly effects: readonly TallyEffect[];
  /** The luck stream after the chances rolled (final mode). */
  readonly luck: PcgState | null;
}

/** Largest score a chain can show; beyond it "the ink ran out" (docs/SHOP_DESIGN.md §8.5). */
export const SCORE_CAP = 1e100;

/** What one Appraisal level adds to each pattern or feat (LEDGER.MD §8.3). */
export const APPRAISAL_STEP: Readonly<
  Record<AppraisalTarget, { readonly p: number; readonly m: number }>
> = {
  ladder: { p: 6, m: 2 },
  echo: { p: 4, m: 1 },
  streak: { p: 4, m: 1 },
  stair: { p: 5, m: 1 },
  prism: { p: 5, m: 1 },
  mirror: { p: 6, m: 2 },
  fullCircle: { p: 8, m: 2 },
  none: { p: 3, m: 1 },
  capture: { p: 3, m: 0 },
  crown: { p: 6, m: 0 },
  landing: { p: 1, m: 0 },
  epitaph: { p: 5, m: 1 },
};

const PATTERN_ORDER: readonly PatternName[] = [
  'ladder',
  'mirror',
  'echo',
  'streak',
  'stair',
  'prism',
];

/** Every pattern a list of faces makes, with its bonus (§11.1 and the hidden patterns). */
export function patternsOf(
  faces: readonly Face[],
  hidden: readonly HiddenPattern[],
  c: Pick<Constants, 'BONUS_LADDER' | 'BONUS_ECHO' | 'BONUS_STREAK'>,
): { pattern: PatternName; bonus: number }[] {
  if (faces.length < 2) return [];
  const first = faces[0] as Face;
  const last = faces[faces.length - 1] as Face;
  const sameColor = faces.every((f) => f.color === first.color);
  const sameNumber = faces.every((f) => f.number === first.number);
  const ascending =
    faces.length === 3 &&
    faces[0]?.number === 1 &&
    faces[1]?.number === 2 &&
    faces[2]?.number === 3;
  const out: { pattern: PatternName; bonus: number }[] = [];
  if (ascending && sameColor) out.push({ pattern: 'ladder', bonus: c.BONUS_LADDER });
  if (sameNumber) out.push({ pattern: 'echo', bonus: c.BONUS_ECHO });
  if (sameColor) out.push({ pattern: 'streak', bonus: c.BONUS_STREAK });
  if (hidden.includes('stair') && ascending && !sameColor) out.push({ pattern: 'stair', bonus: 1 });
  if (
    hidden.includes('prism') &&
    faces.length >= 3 &&
    new Set(faces.map((f) => f.color)).size === faces.length
  )
    out.push({ pattern: 'prism', bonus: 1 });
  if (
    hidden.includes('mirror') &&
    faces.length >= 3 &&
    first.color === last.color &&
    first.number === last.number
  )
    out.push({ pattern: 'mirror', bonus: 2 });
  return out;
}

const chebyshev = (a: Square, b: Square): number =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

const isCorner = (sq: Square): boolean => (sq.x === 0 || sq.x === 5) && (sq.y === 0 || sq.y === 5);
const isEdge = (sq: Square): boolean => sq.x === 0 || sq.x === 5 || sq.y === 0 || sq.y === 5;
const isCentre = (sq: Square): boolean => (sq.x === 2 || sq.x === 3) && (sq.y === 2 || sq.y === 3);
const onOwnHalf = (side: Side, sq: Square): boolean => (side === 'S' ? sq.y <= 2 : sq.y >= 3);

/** Mantel slots a kit has (base 5; Fixtures and Pledges change it). */
export const mantelSlots = (kit: KitSpec | undefined): number => kit?.slots ?? 5;

class Counter {
  p = 0;
  /** M in tenths. */
  m10 = 0;
  readonly steps: TallyStep[] = [];

  push(
    source: TallySourceKind,
    op: TallyOp,
    amount: number,
    extra: Partial<Pick<TallyStep, 'id' | 'uid' | 'chance' | 'pending'>> = {},
  ): void {
    if (!extra.pending) {
      if (op === 'p') this.p += amount;
      else if (op === 'm') this.m10 += Math.round(amount * 10);
      else if (op === 'x') this.m10 = Math.floor(this.m10 * amount + 1e-9);
    }
    this.steps.push({ source, op, amount, p: this.p, m: this.m10 / 10, ...extra });
  }
}

interface Ctx {
  readonly s: MatchState;
  readonly c: Constants;
  readonly side: Side;
  readonly input: TallyInput;
  readonly mode: 'preview' | 'final';
  readonly t: Counter;
  readonly effects: TallyEffect[];
  rng: Pcg32 | null;
  readonly faces: readonly Face[];
  readonly captures: number;
  readonly crowns: number;
  readonly landings: number;
  readonly pattern: PatternName | null;
  readonly fullCircle: boolean;
  /** Chance multiplier: Rabbit's Foot doubles every chance. */
  readonly luckFactor: number;
}

/** Rolls `hits` in `outOf`, or leaves it pending in a preview. */
function chance(ctx: Ctx, hits: number, outOf: number): 'hit' | 'miss' | 'pending' {
  const h = Math.min(outOf, hits * ctx.luckFactor);
  if (ctx.mode === 'preview') return 'pending';
  if (!ctx.rng) ctx.rng = Pcg32.fromState(L(ctx.s).luck);
  return ctx.rng.nextBelow(outOf) < h ? 'hit' : 'miss';
}

/** Applies one Superstition; returns whether it added anything. */
function superstition(ctx: Ctx, item: ItemSpec, as: string = item.id): boolean {
  const { t, side, input, s, faces } = ctx;
  const src = { id: as, uid: item.uid } as const;
  const P = (n: number): boolean => {
    if (n === 0) return false;
    t.push('superstition', 'p', n, src);
    return true;
  };
  const M = (n: number): boolean => {
    if (n === 0) return false;
    t.push('superstition', 'm', n, src);
    return true;
  };
  const X = (f: number): boolean => {
    if (f === 1) return false;
    t.push('superstition', 'x', f, src);
    return true;
  };
  const brass = (n: number, cap?: { key: string; max: number }): boolean => {
    if (n <= 0) return false;
    t.push('superstition', 'brass', n, src);
    ctx.effects.push({ kind: 'brass', amount: n, reason: as, ...(cap ? { cap } : {}) });
    return true;
  };
  const byColor = (color: Face['color']) => faces.filter((f) => f.color === color).length;
  const ownOnBoard = onBoardPawns(s.pawns, side);
  const end = input.end;
  switch (as) {
    case 'red-thread':
      return P(3 * byColor('R'));
    case 'blue-bead':
      return P(3 * byColor('B'));
    case 'four-leaf':
      return P(4 * byColor('G'));
    case 'canary-feather':
      return P(
        2 *
          input.links
            .filter((l) => l.face.color === 'Y')
            .reduce((n, l) => n + l.landings.length, 0),
      );
    case 'odd-fellow':
      return faces.length > 0 && faces.every((f) => f.number !== 2) ? M(3) : false;
    case 'even-keel':
      return faces.length > 0 && faces.every((f) => f.number === 2) ? M(4) : false;
    case 'snap': {
      const f = faces[0];
      const top = input.topBefore;
      return f && top && f.color === top.color && f.number === top.number ? M(3) : false;
    }
    case 'bloodline': {
      const colors = [...L(s).round.colors[side], ...faces.map((f) => f.color)];
      return colors.length > 0 && colors.every((c) => c === colors[0]) ? X(2) : false;
    }
    case 'ladder-walker':
      return ctx.pattern === 'ladder' ? M(5) : false;
    case 'mirror-twin':
      return ctx.pattern === 'echo' ? M(2) : false;
    case 'ribbon':
      return ctx.pattern === 'streak' ? M(2) : false;
    case 'plumb-line':
      return ctx.pattern === null ? M(2) : false;
    case 'compass-rose':
      return ctx.fullCircle ? M(3) : false;
    case 'stairwell':
      return ctx.pattern === 'stair' ? M(2) : false;
    case 'prism':
      return ctx.pattern === 'prism' ? M(2) : false;
    case 'palindrome':
      return ctx.pattern === 'mirror' ? M(2) : false;
    case 'horseshoe':
      return ctx.captures > 0 ? M(2) : false;
    case 'black-cat':
      return P(
        8 * input.links.reduce((n, l) => n + l.captures.filter((c) => c.inscribed).length, 0),
      );
    case 'bottle-tree':
      return M(ctx.captures);
    case 'crows-feather':
      return brass(ctx.captures, { key: 'crows-feather', max: 4 });
    case 'butchers-hook': {
      let value = item.value ?? 1;
      if (ctx.captures >= 2) {
        value += 0.25;
        ctx.effects.push({ kind: 'grow', uid: item.uid, value });
      }
      return X(value);
    }
    case 'paper-crown':
      return P(8 * ctx.crowns);
    case 'penny-on-the-track':
      return brass(ctx.crowns, { key: 'penny-on-the-track', max: 3 });
    case 'homecoming':
      return input.crownedAtEnd && end && end.y === homeRow(side) ? M(4) : false;
    case 'black-mirror':
      return input.crownedAtStart ? X(2) : false;
    case 'tallow-candle': {
      let value = item.value ?? 1;
      if (ctx.crowns > 0) {
        value = Math.round((value + 0.2 * ctx.crowns) * 100) / 100;
        ctx.effects.push({ kind: 'grow', uid: item.uid, value });
      }
      return X(value);
    }
    case 'pawn-kings-crown': {
      let value = item.value ?? 1;
      if (ctx.crowns > 0) {
        value += 0.5 * ctx.crowns;
        ctx.effects.push({ kind: 'grow', uid: item.uid, value });
      }
      return X(value);
    }
    case 'mourning-bell':
      return M(L(s).round.lost[side]);
    case 'gravediggers-spade':
      return input.source === 'epitaph' ? X(2) : false;
    case 'rag-and-bone':
      return P(6 * input.links.filter((l) => l.wasWild).length);
    case 'debt-collector':
      return M(counter(s, side, 'pawned'));
    case 'sacrificial-lamb': {
      const off = Object.values(s.pawns).filter(
        (p) => p.side === side && p.status !== 'onBoard',
      ).length;
      return X(1 + 0.5 * off);
    }
    case 'magpies-hoard':
      return M(Math.floor(Math.max(0, L(s).brass[side]) / 5));
    case 'first-light':
      return L(s).round.chains[side] === 0 ? X(2) : false;
    case 'last-orders':
      return s.players[side === 'S' ? 'N' : 'S'].passed ? M(3) : false;
    case 'spilled-salt':
      return M(Math.floor(input.pileSize / 3));
    case 'seventh-son':
      return (counter(s, side, 'chains') + 1) % 7 === 0 ? X(3) : false;
    case 'grandfather-clock':
      return M(L(s).round.turns[side]);
    case 'thirteenth-step':
      return input.links.length === 1 ? M(4) : false;
    case 'long-shadow':
      return input.links.length >= 3 ? M(2) : false;
    case 'salt-line':
      return end && onOwnHalf(side, end) ? P(6) : false;
    case 'cornerstone':
      return end ? (isCorner(end) ? M(4) : isEdge(end) ? M(1) : false) : false;
    case 'crossroads':
      return P(4 * input.links.reduce((n, l) => n + l.landings.filter(isCentre).length, 0));
    case 'phalanx':
      return end
        ? M(
            ownOnBoard.filter(
              (p) => p.id !== input.pawnId && p.square && chebyshev(p.square, end) === 1,
            ).length,
          )
        : false;
    case 'tallow-stub':
      return M(ownOnBoard.filter((p) => p.square?.y === homeRow(side)).length);
    case 'long-way-home':
      return P(2 * input.links.reduce((n, l) => n + l.path, 0));
    case 'lucky-coin': {
      const r = chance(ctx, 1, 3);
      if (r === 'pending') {
        t.push('superstition', 'brass', 2, {
          ...src,
          pending: { hits: Math.min(3, ctx.luckFactor), outOf: 3 },
        });
        return false;
      }
      if (r === 'miss') return false;
      t.push('superstition', 'brass', 2, { ...src, chance: true });
      ctx.effects.push({ kind: 'brass', amount: 2, reason: as });
      return true;
    }
    case 'loaded-die': {
      const r = chance(ctx, 1, 3);
      if (r === 'pending') {
        t.push('superstition', 'x', 2, {
          ...src,
          pending: { hits: Math.min(3, ctx.luckFactor), outOf: 3 },
        });
        return false;
      }
      if (r === 'miss') return false;
      t.push('superstition', 'x', 2, { ...src, chance: true });
      return true;
    }
    case 'empty-chair': {
      const kit = s.config.ledger?.kits[side];
      const used = mantelOf(s, side).filter((i) => i.finish !== 'ghost').length;
      return X(Math.max(1, mantelSlots(kit) - used + 1));
    }
    case 'the-unredeemed':
      return input.source === 'epitaph'
        ? X(1.5 ** Math.max(1, counter(s, side, 'epitaphsThisTable')))
        : false;
    case 'the-last-pawn':
      return ownOnBoard.length === 1 ? X(4) : false;
    default:
      return false;
  }
}

/** Items that copy (Mimic Jar, Reliquary) cannot copy each other. */
const COPIERS = new Set(['mimic-jar', 'reliquary']);

function finishOf(
  ctx: Ctx,
  finish: string | undefined,
  on: 'card' | 'item',
  id?: string,
  uid?: string,
): void {
  if (!finish) return;
  const extra = { id: finish, ...(uid ? { uid } : {}) };
  switch (finish) {
    case 'foil':
      // +10 P, on a card or a Superstition alike (it was +15 on one: issue #10).
      ctx.t.push('finish', 'p', 10, extra);
      break;
    case 'gilt':
      ctx.t.push('finish', 'm', on === 'card' ? 3 : 4, extra);
      break;
    case 'pearl':
      ctx.t.push('finish', 'x', 1.5, extra);
      break;
    default:
      break;
  }
  void id;
}

/** Link points (step 1) for one link, honoring Salted Earth, Tarnish and Rust. */
function linkPoints(
  ctx: Ctx,
  link: LedgerLink,
  count: (src: TallySourceKind, n: number) => void,
): void {
  const { s, side, c } = ctx;
  const tarnish = houseRule(s, 'tarnish', side);
  if (tarnish?.color === link.face.color) return;
  if (hasRule(s, 'rust', side) && L(s).facesPlayed[side].includes(faceCode(link.face))) return;
  if (!hasRule(s, 'salted-earth', side))
    for (let i = 0; i < link.newLandings; i++) count('landing', c.POINTS_LANDING);
  for (const cap of link.captures) {
    count('capture', c.POINTS_CAPTURE);
    if (cap.exposed) count('status', 5);
  }
  if (link.crowned && s.config.crownMode === 'immediate') count('crown', c.POINTS_CROWN);
}

const stampOf = (s: MatchState, cardId: string): StampId | undefined =>
  s.ledger?.cardStamps[cardId] ?? s.cards[cardId]?.stamp;

/** Counts a chain or Epitaph replay (LEDGER.MD). `final` rolls chances and reports effects. */
/** Something that wants to see every chain as it is really scored (see `watchFinalTallies`). */
let finalWatch: ((s: MatchState, input: TallyInput) => void) | null = null;

/**
 * Sees every chain as it is really scored: the state then and what was counted. The Counter
 * replays the last table with this set to recount its best chain with an item added ("what this
 * would have added", docs/LONG_NIGHT_ART_DIRECTION.md §11.9). Null stops watching. The engine
 * stays pure: the watcher only reads.
 */
export function watchFinalTallies(fn: ((s: MatchState, input: TallyInput) => void) | null): void {
  finalWatch = fn;
}

export function tally(s: MatchState, input: TallyInput, mode: 'preview' | 'final'): TallyResult {
  if (mode === 'final' && finalWatch) finalWatch(s, input);
  const c = s.config.constants;
  const side = input.side;
  const t = new Counter();
  const faces = input.links.map((l) => l.face);
  const captures = input.links.reduce((n, l) => n + l.captures.length, 0);
  const crowns = input.links.filter((l) => l.crowned).length;
  const landings = input.links.reduce((n, l) => n + l.newLandings, 0);

  // Patterns (with Appraisals deciding which counts as best) and Full Circle.
  const hidden = discovered(s, side);
  const valueOf = (p: { pattern: PatternName; bonus: number }) => {
    const lvl = appraisal(s, side, p.pattern) - 1;
    return (p.bonus + lvl * APPRAISAL_STEP[p.pattern].m) * 1000 + lvl * APPRAISAL_STEP[p.pattern].p;
  };
  const candidates = patternsOf(faces, hidden, c).sort(
    (a, b) =>
      valueOf(b) - valueOf(a) ||
      PATTERN_ORDER.indexOf(a.pattern) - PATTERN_ORDER.indexOf(b.pattern),
  );
  const repeats = hasRule(s, 'no-repeats', side) ? L(s).round.patterns[side] : [];
  const usable = candidates.filter((p) => !repeats.includes(p.pattern));
  const counted = usable.slice(0, mantelOf(s, side).some((i) => i.id === 'double-knot') ? 2 : 1);
  const best = counted[0] ?? null;
  const compass = mantelOf(s, side).some((i) => i.id === 'compass-rose');
  const end = input.end;
  const returned =
    !!end && (sameSquare(input.start, end) || (compass && chebyshev(input.start, end) <= 1));
  const allLandings = input.links.flatMap((l) => l.landings);
  const fullCircle = returned && !isCollinear(input.start, allLandings);

  const ctx: Ctx = {
    s,
    c,
    side,
    input,
    mode,
    t,
    effects: [],
    rng: null,
    faces,
    captures,
    crowns,
    landings,
    pattern: best?.pattern ?? null,
    fullCircle,
    luckFactor: mantelOf(s, side).some((i) => i.id === 'rabbits-foot') ? 2 : 1,
  };
  const pawn = s.pawns[input.pawnId];

  // 1. Links.
  for (const link of input.links) {
    linkPoints(ctx, link, (src, n) => t.push(src, 'p', n));
    for (const o of link.omens) {
      if (o.kind === 'candle') t.push('omen', 'm', 1, { id: o.kind });
      else if (o.kind === 'chalk-x' && link.face.number === 3)
        t.push('omen', 'p', 10, { id: o.kind });
      else if (o.kind === 'ink-pool') t.push('omen', 'p', 3, { id: o.kind });
      else if (o.kind === 'altar' && link.crowned) t.push('omen', 'm', 2, { id: o.kind });
    }
  }

  // 2. Shape.
  for (let i = 0; i < input.links.length; i++) t.push('link', 'm', 1);
  for (const pt of counted) {
    t.push('pattern', 'm', pt.bonus, { id: pt.pattern });
    const lvl = appraisal(s, side, pt.pattern) - 1;
    if (lvl > 0) {
      const step = APPRAISAL_STEP[pt.pattern];
      t.push('appraisal', 'p', lvl * step.p, { id: pt.pattern });
      t.push('appraisal', 'm', lvl * step.m, { id: pt.pattern });
    }
  }
  if (fullCircle) {
    t.push('fullCircle', 'm', c.BONUS_FULL_CIRCLE);
    const lvl = appraisal(s, side, 'fullCircle') - 1;
    if (lvl > 0) {
      t.push('appraisal', 'p', lvl * APPRAISAL_STEP.fullCircle.p, { id: 'fullCircle' });
      t.push('appraisal', 'm', lvl * APPRAISAL_STEP.fullCircle.m, { id: 'fullCircle' });
    }
  }
  const feat = (target: AppraisalTarget, times: number) => {
    const lvl = appraisal(s, side, target) - 1;
    if (lvl <= 0 || times <= 0) return;
    const step = APPRAISAL_STEP[target];
    if (step.p) t.push('appraisal', 'p', lvl * step.p * times, { id: target });
    if (step.m) t.push('appraisal', 'm', lvl * step.m * times, { id: target });
  };
  if (!best) feat('none', 1);
  feat('capture', captures);
  feat('crown', crowns);
  feat('landing', landings);
  if (input.source === 'epitaph') feat('epitaph', 1);

  // 3. Cards: Stamps, then Finishes.
  if (!hasRule(s, 'gleam', side))
    for (const link of input.links) {
      const cardId = link.cardId;
      if (!cardId) continue;
      const card = s.cards[cardId];
      const stamp = stampOf(s, cardId);
      const extra = { id: stamp as string, uid: cardId };
      switch (stamp) {
        case 'brass-stamp':
          t.push('stamp', 'p', 5, extra);
          break;
        case 'wax-seal':
          t.push('stamp', 'm', 2, extra);
          break;
        case 'coin-stamp':
          t.push('stamp', 'brass', 1, extra);
          ctx.effects.push({ kind: 'brass', amount: 1, reason: 'coin-stamp' });
          break;
        case 'double-stamp':
          linkPoints(ctx, link, (_src, n) => t.push('stamp', 'p', n, extra));
          break;
        case 'glass-stamp': {
          t.push('stamp', 'x', 2, extra);
          const r = chance(ctx, 1, 4);
          if (r === 'hit') ctx.effects.push({ kind: 'shatter', cardId });
          break;
        }
        case 'lucky-stamp': {
          const big = chance(ctx, 1, 5);
          if (big === 'pending')
            t.push('stamp', 'p', 20, {
              ...extra,
              pending: { hits: Math.min(5, ctx.luckFactor), outOf: 5 },
            });
          else if (big === 'hit') t.push('stamp', 'p', 20, { ...extra, chance: true });
          const rich = chance(ctx, 1, 15);
          if (rich === 'pending')
            t.push('stamp', 'brass', 10, {
              ...extra,
              pending: { hits: Math.min(15, ctx.luckFactor), outOf: 15 },
            });
          else if (rich === 'hit') {
            t.push('stamp', 'brass', 10, { ...extra, chance: true });
            ctx.effects.push({ kind: 'brass', amount: 10, reason: 'lucky-stamp' });
          }
          break;
        }
        case 'tally-stamp': {
          const value = s.ledger?.cardValues[cardId] ?? card?.value ?? 1;
          t.push('stamp', 'p', value, extra);
          ctx.effects.push({ kind: 'cardValue', cardId, value: value + 1 });
          break;
        }
        default:
          break;
      }
      finishOf(ctx, card?.finish, 'card', cardId, cardId);
    }

  // 4. The pawn and the side.
  if (pawn) {
    if (hasCarving(pawn, 'tusk') && captures > 0)
      t.push('carving', 'p', 4 * captures, { id: 'tusk' });
    if (hasCarving(pawn, 'glutton')) {
      const value = carvingValue(s, pawn, 'glutton');
      if (value > 0) t.push('carving', 'p', value, { id: 'glutton' });
      if (captures > 0)
        ctx.effects.push({ kind: 'pawnValue', key: `${pawn.id}:glutton`, value: value + captures });
    }
    if (hasCarving(pawn, 'dowser')) {
      const omens = input.links.reduce((n, l) => n + l.omens.length, 0);
      if (omens > 0) t.push('carving', 'p', 3 * omens, { id: 'dowser' });
    }
    if (hasCarving(pawn, 'bellwether')) t.push('carving', 'm', 1, { id: 'bellwether' });
    const kindled = statusOf(s, pawn.id, 'kindled');
    if (kindled && (kindled.n ?? 0) > 0) {
      const all = input.links.reduce((n, l) => n + l.landings.length, 0);
      if (all > 0) t.push('status', 'p', 2 * (kindled.n ?? 0) * all, { id: 'kindled' });
      ctx.effects.push({ kind: 'kindled', pawnId: pawn.id, n: (kindled.n ?? 0) - 1 });
    }
    if (statusOf(s, pawn.id, 'cursed')) t.push('status', 'x', 0.5, { id: 'cursed' });
  }
  if (input.source === 'epitaph' && input.epitaphSource) {
    const from = s.pawns[input.epitaphSource];
    if (from && hasCarving(from, 'scribes-mark')) t.push('carving', 'm', 2, { id: 'scribes-mark' });
  }
  if (sideStatus(s, side, 'inspired')) {
    t.push('status', 'm', 2, { id: 'inspired' });
    ctx.effects.push({ kind: 'useSideStatus', status: 'inspired' });
  }
  if (sideStatus(s, side, 'black-candle')) {
    t.push('status', 'x', 2, { id: 'black-candle' });
    ctx.effects.push({ kind: 'useSideStatus', status: 'black-candle' });
  }

  // 5. The Mantel, left to right.
  const snuffed = hasRule(s, 'snuffed', side) && input.links.length === 1;
  const mantel = mantelOf(s, side);
  if (!snuffed)
    mantel.forEach((item, i) => {
      let copied: ItemSpec | undefined;
      if (item.id === 'mimic-jar') copied = mantel[i + 1];
      else if (item.id === 'reliquary') copied = mantel[0];
      if (copied && (COPIERS.has(copied.id) || copied.uid === item.uid)) copied = undefined;
      const fired = copied ? superstition(ctx, item, copied.id) : superstition(ctx, item);
      if (fired) ctx.effects.push({ kind: 'trigger', uid: item.uid });
      finishOf(ctx, item.finish, 'item', item.id, item.uid);
    });

  // 6. The score.
  const raw = Math.floor((t.p * t.m10) / 10);
  const score = Math.max(0, Math.min(SCORE_CAP, raw));
  const brass = t.steps
    .filter((st) => st.op === 'brass' && !st.pending)
    .reduce((n, st) => n + st.amount, 0);
  const patternBonus = counted.reduce((n, p) => n + p.bonus, 0);
  const breakdown: ScoreBreakdown = {
    points: {
      landings,
      landing: landings * c.POINTS_LANDING,
      captures,
      capture: captures * c.POINTS_CAPTURE,
      crown: crowns > 0 && s.config.crownMode === 'immediate' ? c.POINTS_CROWN : 0,
      total: t.p,
    },
    multiplier: {
      links: input.links.length,
      pattern: best?.pattern ?? null,
      patternBonus,
      fullCircle,
      fullCircleBonus: fullCircle ? c.BONUS_FULL_CIRCLE : 0,
      total: t.m10 / 10,
      secondPattern: counted[1]?.pattern ?? null,
    },
    score,
    steps: t.steps,
    brass,
  };
  return { breakdown, effects: ctx.effects, luck: ctx.rng ? ctx.rng.toState() : null };
}
